import type { Locale } from '../types'

/**
 * Client revision copy (Bee, Community, visibility, biomarkers, language).
 * Merged into the single `t()` lookup by LocaleContext. Any "XP" written here
 * is rendered as "Bee" by the terminology layer (i18n/terms.ts).
 */
export const revisionStrings: Record<Locale, Record<string, string>> = {
  'pt-BR': {
    /* Navigation */
    'nav.community': 'Comunidade',
    'nav.progress': 'Progresso',
    'nav.stats': 'Progresso',
    'stats.title': 'Progresso',
    'common.xp': 'Bee',

    /* Home */
    'home.summary.nectar': 'Bee hoje',
    'home.summary.info': 'Como o Life Score funciona',
    'home.streak.next': '{badge} em {n} dias',
    'home.insight.title': 'Sua semana',
    'home.insight.avg': 'Life Score médio: {n} nos últimos 7 dias',
    'home.insight.strong': '{n} de 7 dias na faixa forte',
    'home.insight.cta': 'Ver Progresso',
    'home.teaser.title': '{n} novos Pulses',
    'home.teaser.titleOne': '1 novo Pulse',
    'home.teaser.body': 'Veja novidades da sua comunidade',
    'home.teaser.cta': 'Abrir',

    /* Life Score info */
    'lsinfo.title': 'Como o Life Score funciona',
    'lsinfo.body':
      'O Life Score de hoje vai de 0 a 100 e reflete as seis áreas do seu Daily Check.',
    'lsinfo.areas': 'O que influencia o dia',
    'lsinfo.periods': 'Semana, Mês e Ano',
    'lsinfo.periodsBody':
      'Resumem a evolução dos seus dias nesses períodos, pela média das pontuações diárias.',
    'lsinfo.nectar':
      'Life Score e Bee são diferentes: o Bee acumula o seu progresso; o Life Score mostra como foi o dia.',
    'lsinfo.disclaimer': 'É um indicador de hábitos, não um diagnóstico médico.',
    'lsinfo.close': 'Entendi',

    /* Community */
    'community.title': 'Comunidade',
    'community.subtitle': 'Pessoas, Pulse e comunidades por afinidade.',
    'community.tab.feed': 'Para você',
    'community.tab.communities': 'Comunidades',
    'community.tab.discover': 'Descobrir',
    'community.feed.posts': 'Publicações recentes',
    'community.mine': 'Minhas comunidades',
    'community.discover': 'Descobrir comunidades',
    'community.members': '{n} membros',
    'community.weekly': '{n} posts esta semana',
    'community.join': 'Participar',
    'community.joined': 'Participando',
    'community.none.title': 'Você ainda não participa de nenhuma',
    'community.none.body':
      'Escolha comunidades pelos esportes e hábitos que fazem parte da sua rotina.',
    'community.detail.posts': 'Posts',
    'community.detail.pulse': 'Pulse',
    'community.detail.members': 'Membros',
    'community.noPosts.title': 'Nada publicado ainda',
    'community.noPosts.body': 'Os posts desta comunidade aparecem aqui.',
    'community.noPulse.title': 'Nenhum Pulse ativo agora',
    'community.noPulse.body': 'Pulses de membros desta comunidade aparecem aqui.',
    'community.missing': 'Esta comunidade não está disponível.',
    'community.create': 'Criar Pulse ou Post',

    /* Create — first-day exception */
    'create.post.firstDay': 'Seu primeiro Post está liberado hoje',
    'create.post.firstDayBody':
      'Presente de boas-vindas no dia em que sua conta foi criada. A partir de amanhã, o Post abre com {gate} XP no dia.',

    /* Public visibility */
    'visibility.title': 'O que aparece no meu perfil público',
    'visibility.subtitle':
      'Conquistas e sequência começam visíveis. Performance, progresso, métricas corporais e biomarcadores começam ocultos.',
    'visibility.pickEach': 'Escolha cada métrica que pode aparecer',
    'visibility.noneSelected': 'Nenhuma métrica selecionada para exibição pública.',
    'visibility.chooseMetrics': 'Escolher métricas',
    'visibility.health': 'Dados de saúde',
    'visibility.healthNote':
      'Privados por padrão. Só ficam públicos com o seu consentimento explícito.',
    'visibility.preview': 'Ver meu perfil público',
    'visibility.pickSport': 'Escolher métricas esportivas',
    'visibility.pickBio': 'Escolher biomarcadores',
    'preview.title': 'Seu perfil público',
    'preview.banner':
      'É assim que outras pessoas veem você. Só aparece o que você liberou.',
    'preview.edit': 'Ajustar visibilidade',
    'preview.streak': 'Sequência de {n} dias',
    'preview.progress': 'Progresso',
    'preview.progressValue': 'Life Score médio de {n} nos últimos 30 dias',
    'preview.body': 'Composição corporal',
    'preview.biomarkers': 'Biomarcadores',
    'preview.nothing': 'Nada selecionado para esta seção.',
    'preview.hiddenAll':
      'Conquistas, métricas e dados de saúde estão ocultos. Seu perfil mostra apenas nome, bio e posts.',

    /* Health hub */
    'health.title': 'Saúde',
    'health.subtitle': 'Composição corporal, exames e performance.',
    'health.body': 'Composição corporal (BIA)',
    'health.bodyHint': 'Peso, gordura, massa magra, água, BMR e IMC',
    'health.biomarkers': 'Biomarcadores e exames',
    'health.biomarkersHint': 'Resultados laboratoriais registrados por você',
    'health.sports': 'Performance esportiva',
    'health.sportsHint': 'Corrida, ciclismo, natação, CrossFit e musculação',
    'health.progress': 'Gráficos de progresso',
    'health.progressHint': 'Evolução corporal e esportiva ao longo do tempo',
    'health.private': 'Tudo nesta área é privado até você decidir o contrário.',

    /* Biomarkers */
    'bio.title': 'Biomarcadores',
    'bio.subtitle': 'Resultados de exames laboratoriais. Privados por padrão.',
    'bio.last': 'Último resultado',
    'bio.none': 'Sem resultado registrado',
    'bio.add': 'Adicionar resultado',
    'bio.value': 'Valor',
    'bio.unit': 'Unidade',
    'bio.displayUnit': 'Exibir em',
    'bio.date': 'Data da coleta',
    'bio.source': 'Laboratório ou origem (opcional)',
    'bio.save': 'Salvar resultado',
    'bio.cancel': 'Cancelar',
    'bio.history': 'Histórico',
    'bio.public': 'Mostrar no perfil público',
    'bio.publicHint': 'Desligado por padrão. Exige o seu consentimento.',
    'bio.masterOff':
      'Para aparecer no perfil, ative também "Biomarcadores" em Perfil público.',
    'bio.disclaimer':
      'O BEELYV registra e organiza seus resultados. Não classifica, interpreta nem substitui avaliação médica.',
    'bio.future': 'Novos biomarcadores entram nesta mesma lista.',
    'bio.private': 'Privado',
    'bio.publicTag': 'Público',
    'bio.entered': 'Registrado em {unit}',
    'bio.invalid': 'Informe um valor numérico maior que zero.',
    'bio.saved': 'Resultado salvo',
    'bio.lab': 'Laboratório de análises',

    /* Language */
    'language.system': 'Idioma do dispositivo',
    'language.systemHint': 'Detectado: {lang}',
    'language.manualNote':
      'Uma escolha manual substitui o idioma do dispositivo até você voltar a segui-lo.',
    'entry.lang.detected': 'Pré-selecionado a partir do idioma do seu dispositivo.',

    /* Hydration */
    'hydration.target': 'Meta diária sugerida',
    'hydration.progress': '{intake} de {target}',
    'hydration.goal': 'Meta diária sugerida: {target}',
    'hydration.goalNoWeight': 'Informe seu peso para uma meta personalizada',
    'hydration.manualMark': 'Marcar como concluído manualmente',
    'hydration.manualUndo': 'Desfazer confirmação manual',
    'hydration.manualDone': 'Concluído manualmente',
    'metrics.notSet': 'Não informado',
    'hydration.noWeight': 'Informe seu peso para calcular sua meta personalizada de hidratação.',
    'hydration.addWeight': 'Adicionar peso',
    'hydration.info.open': 'Como a meta de hidratação é calculada',
    'hydration.info.title': 'Sua meta de hidratação',
    'hydration.info.body':
      'Sua meta-base é calculada usando aproximadamente {ml} ml por kg de peso corporal.',
    'hydration.info.formula': '{kg} kg × {ml} ml = {target} por dia',
    'hydration.disclaimer':
      'Sua necessidade pode variar conforme rotina, atividade física e orientação profissional.',

    /* Connected services */
    'services.planned': 'Previsto',
    'services.cap.importActivities': 'Importar atividades',
    'services.cap.importHealth': 'Importar dados de saúde',
    'services.cap.crossPost': 'Compartilhar posts',
    'services.privacy':
      'Atividades e dados importados seguem suas configurações de privacidade e nunca ficam públicos automaticamente.',
    'share.title': 'Compartilhar também em serviços conectados',
    'share.body':
      'Opcional. Você escolhe onde publicar; nada é compartilhado fora do BEELYV automaticamente.',
    'share.always': 'Sempre',
  },

  'en-US': {
    'nav.community': 'Community',
    'nav.progress': 'Progress',
    'nav.stats': 'Progress',
    'stats.title': 'Progress',
    'common.xp': 'Bee',

    'home.summary.nectar': 'Bee Today',
    'home.summary.info': 'How the Life Score works',
    'home.streak.next': '{badge} in {n} days',
    'home.insight.title': 'Your week',
    'home.insight.avg': 'Average Life Score: {n} over the last 7 days',
    'home.insight.strong': '{n} of 7 days in the strong band',
    'home.insight.cta': 'Open Progress',
    'home.teaser.title': '{n} new Pulses',
    'home.teaser.titleOne': '1 new Pulse',
    'home.teaser.body': "See what's new in your community",
    'home.teaser.cta': 'Open',

    'lsinfo.title': 'How the Life Score works',
    'lsinfo.body':
      "Today's Life Score goes from 0 to 100 and reflects the six areas of your Daily Check.",
    'lsinfo.areas': 'What shapes the day',
    'lsinfo.periods': 'Week, Month and Year',
    'lsinfo.periodsBody':
      'They summarize how your days evolved over those periods, using the average of daily scores.',
    'lsinfo.nectar':
      'Life Score and Bee are different: Bee accumulates your progress; the Life Score shows how the day went.',
    'lsinfo.disclaimer': 'It is a habit indicator, not a medical diagnosis.',
    'lsinfo.close': 'Got it',

    'community.title': 'Community',
    'community.subtitle': 'People, Pulse and interest communities.',
    'community.tab.feed': 'For you',
    'community.tab.communities': 'Communities',
    'community.tab.discover': 'Discover',
    'community.feed.posts': 'Recent posts',
    'community.mine': 'My communities',
    'community.discover': 'Discover communities',
    'community.members': '{n} members',
    'community.weekly': '{n} posts this week',
    'community.join': 'Join',
    'community.joined': 'Joined',
    'community.none.title': 'You have not joined any yet',
    'community.none.body': 'Pick communities around the sports and habits in your routine.',
    'community.detail.posts': 'Posts',
    'community.detail.pulse': 'Pulse',
    'community.detail.members': 'Members',
    'community.noPosts.title': 'Nothing posted yet',
    'community.noPosts.body': "This community's posts show up here.",
    'community.noPulse.title': 'No active Pulse right now',
    'community.noPulse.body': 'Pulses from members of this community show up here.',
    'community.missing': 'This community is not available.',
    'community.create': 'Create Pulse or Post',

    'create.post.firstDay': 'Your first Post is unlocked today',
    'create.post.firstDayBody':
      'A welcome gift on the day your account was created. From tomorrow, Post opens at {gate} XP today.',

    'visibility.title': 'What appears on my public profile',
    'visibility.subtitle':
      'Achievements and streak start visible. Performance, progress, body metrics and biomarkers start hidden.',
    'visibility.pickEach': 'Choose each metric that may appear',
    'visibility.noneSelected': 'No metrics selected for public display.',
    'visibility.chooseMetrics': 'Choose metrics',
    'visibility.health': 'Health data',
    'visibility.healthNote':
      'Private by default. Only becomes public with your explicit consent.',
    'visibility.preview': 'View my public profile',
    'visibility.pickSport': 'Choose sport metrics',
    'visibility.pickBio': 'Choose biomarkers',
    'preview.title': 'Your public profile',
    'preview.banner': 'This is how other people see you. Only what you allowed appears.',
    'preview.edit': 'Adjust visibility',
    'preview.streak': '{n}-day streak',
    'preview.progress': 'Progress',
    'preview.progressValue': 'Average Life Score of {n} over the last 30 days',
    'preview.body': 'Body composition',
    'preview.biomarkers': 'Biomarkers',
    'preview.nothing': 'Nothing selected for this section.',
    'preview.hiddenAll':
      'Achievements, metrics and health data are hidden. Your profile shows only name, bio and posts.',

    'health.title': 'Health',
    'health.subtitle': 'Body composition, lab results and performance.',
    'health.body': 'Body composition (BIA)',
    'health.bodyHint': 'Weight, fat, lean mass, water, BMR and BMI',
    'health.biomarkers': 'Biomarkers and lab results',
    'health.biomarkersHint': 'Lab results you recorded',
    'health.sports': 'Sports performance',
    'health.sportsHint': 'Running, cycling, swimming, CrossFit and strength',
    'health.progress': 'Progress charts',
    'health.progressHint': 'Body and sport evolution over time',
    'health.private': 'Everything here is private until you decide otherwise.',

    'bio.title': 'Biomarkers',
    'bio.subtitle': 'Laboratory results. Private by default.',
    'bio.last': 'Latest result',
    'bio.none': 'No result recorded',
    'bio.add': 'Add result',
    'bio.value': 'Value',
    'bio.unit': 'Unit',
    'bio.displayUnit': 'Show in',
    'bio.date': 'Collection date',
    'bio.source': 'Laboratory or source (optional)',
    'bio.save': 'Save result',
    'bio.cancel': 'Cancel',
    'bio.history': 'History',
    'bio.public': 'Show on public profile',
    'bio.publicHint': 'Off by default. Requires your consent.',
    'bio.masterOff':
      'To appear on your profile, also turn on "Biomarkers" in Public profile.',
    'bio.disclaimer':
      'Beelyv records and organizes your results. It does not classify, interpret or replace medical assessment.',
    'bio.future': 'New biomarkers join this same list.',
    'bio.private': 'Private',
    'bio.publicTag': 'Public',
    'bio.entered': 'Recorded in {unit}',
    'bio.invalid': 'Enter a numeric value greater than zero.',
    'bio.saved': 'Result saved',
    'bio.lab': 'Analysis laboratory',

    'language.system': 'Device language',
    'language.systemHint': 'Detected: {lang}',
    'language.manualNote':
      'A manual choice overrides the device language until you follow it again.',
    'entry.lang.detected': 'Preselected from your device language.',

    'hydration.target': 'Suggested daily goal',
    'hydration.progress': '{intake} of {target}',
    'hydration.goal': 'Suggested daily goal: {target}',
    'hydration.goalNoWeight': 'Add your weight for a personalized goal',
    'hydration.manualMark': 'Mark as completed manually',
    'hydration.manualUndo': 'Undo manual confirmation',
    'hydration.manualDone': 'Completed manually',
    'metrics.notSet': 'Not provided',
    'hydration.noWeight': 'Add your weight to calculate your personalized hydration goal.',
    'hydration.addWeight': 'Add weight',
    'hydration.info.open': 'How the hydration goal is calculated',
    'hydration.info.title': 'Your hydration goal',
    'hydration.info.body':
      'Your baseline goal is calculated using about {ml} ml per kg of body weight.',
    'hydration.info.formula': '{kg} kg × {ml} ml = {target} per day',
    'hydration.disclaimer':
      'Your needs may vary with routine, physical activity and professional guidance.',

    'services.planned': 'Planned',
    'services.cap.importActivities': 'Import activities',
    'services.cap.importHealth': 'Import health data',
    'services.cap.crossPost': 'Share posts',
    'services.privacy':
      'Imported activities and data follow your privacy settings and never become public automatically.',
    'share.title': 'Also share to connected services',
    'share.body':
      "Optional. You choose where to publish; nothing is shared outside Beelyv automatically.",
    'share.always': 'Always',
  },
}
